import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/Card/Card';
import Button from '../../components/Button/Button';
import Loading from '../../components/Loading/Loading';
import api from '../../services/api';
import { EventWithShortCode } from '../../types';
import { formatDateRange, getErrorMessage } from '../../utils/helpers';
import './Dashboard.scss';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<EventWithShortCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchEvents = useCallback(async () => {
    if (!user?.uuid) return;

    try {
      setLoading(true);
      const data = await api.listEvents(user.uuid);
      setEvents(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  if (loading) {
    return <Loading message="Loading dashboard..." />;
  }

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard__header">
          <div>
            <h1>Dashboard</h1>
            <p className="text-muted">Welcome back, {user?.email}</p>
          </div>
          <Link to="/events/new">
            <Button>Create Event</Button>
          </Link>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="dashboard__stats">
          <Card className="stat-card">
            <div className="stat-card__icon">📅</div>
            <div className="stat-card__content">
              <div className="stat-card__value">{events.length}</div>
              <div className="stat-card__label">Total Events</div>
            </div>
          </Card>
        </div>

        <div className="dashboard__section">
          <h2>Recent Events</h2>
          {events.length === 0 ? (
            <Card>
              <p className="text-center text-muted">
                No events yet. Create your first event to get started!
              </p>
              <div className="text-center mt-md">
                <Link to="/events/new">
                  <Button>Create Event</Button>
                </Link>
              </div>
            </Card>
          ) : (
            <div className="dashboard__events">
              {events.slice(0, 5).map((event) => (
                <Link
                  key={event.uuid}
                  to={`/events/${event.uuid}`}
                  className="event-link"
                >
                  <Card hoverable>
                    <h3>{event.title}</h3>
                    <p className="text-muted">{formatDateRange(event.startDate, event.endDate)}</p>
                    {event.description && (
                      <p className="event-description">{event.description}</p>
                    )}
                    <div className="event-shortcode">
                      Code: <strong>{event.shortcode}</strong>
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
          {events.length > 5 && (
            <div className="text-center mt-lg">
              <Link to="/events">
                <Button variant="outline">View All Events</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
